import React, { useMemo, useCallback, useEffect } from "react";
import { Box, Grid, TextField, TextFieldProps } from "@material-ui/core";
import { AccountContact, ContactType } from "../../../api/services";
import { FormSectionHeading } from "../../../components/Forms";
import { useFormContext } from "react-hook-form";
import { isFutureDate } from "../../../utils/validation";
import { DeliveryMethods } from "./DeliveryMethods";
import { PhoneNumber } from "./PhoneNumber";
import { AlertBlock } from "../../../components/AlertBlock";
import { parseDate } from "../../../utils/formatDate";

interface ContactDetailsProps {
  contact: AccountContact;
  fieldProps?: Partial<TextFieldProps>;
  type?: ContactType;
}

export const ContactDetails: React.FC<ContactDetailsProps> = ({
  fieldProps,
  type,
  contact,
}) => {
  const { register, errors, watch, triggerValidation } = useFormContext();

  const billDelivery = watch("billDelivery") || [];
  const commDelivery = watch("commDelivery") || [];
  const modifiedType = contact.type.name !== type?.name;

  const requireEmail = useMemo(() => {
    return billDelivery.includes("Email") || commDelivery.includes("Email");
  }, [billDelivery, commDelivery]);

  const requireSms = useMemo(() => {
    return commDelivery.includes("SMS");
  }, [commDelivery]);

  useEffect(() => {
    // clear up `is required` error if it is not required any more
    if (!requireEmail && errors?.email) {
      triggerValidation("email");
    }
  }, [errors, requireEmail, requireSms, triggerValidation]);

  const emailInputRef = useCallback(
    (input: any) => {
      register(input, {
        required: requireEmail ? "This field is required" : undefined,
        pattern: {
          value: new RegExp("\\S+@\\S+\\.\\S+"),
          message: "Please enter a valid email address.",
        },
      });
    },
    [register, requireEmail],
  );

  return (
    <>
      <FormSectionHeading>BILLING AND COMMUNICATION</FormSectionHeading>
      <Box mt={3} mb={6}>
        <DeliveryMethods {...contact} />
      </Box>
      <FormSectionHeading>CONTACT DETAILS</FormSectionHeading>
      {modifiedType && (
        <Box mb={4}>
          <AlertBlock
            variant="info"
            messages={[
              "You have changed the contact type. Please review the contact details to make sure they are correct.",
            ]}
          />
        </Box>
      )}
      <Box mt={3}>
        <Grid container justify="space-between">
          <Box maxWidth={260} width={0.49}>
            <TextField
              name="email"
              label="Email"
              defaultValue={contact.email}
              required={requireEmail}
              error={!!errors?.email}
              helperText={errors?.email?.message}
              inputProps={{ "data-testid": "ContactDetails__TextField--Email" }}
              {...fieldProps}
              inputRef={emailInputRef}
            />
          </Box>
          <Box maxWidth={260} width={0.49}>
            <PhoneNumber
              id={"1" as any}
              contactType={type}
              contact={contact}
              requireSms={requireSms}
              {...fieldProps}
            />
          </Box>
        </Grid>
      </Box>
      <Box mt={4}>
        <Grid container justify="space-between">
          <Box maxWidth={260} width={0.49}>
            <PhoneNumber
              id={"2" as any}
              contactType={type}
              contact={contact}
              requireSms={requireSms}
              {...fieldProps}
            />
          </Box>
          <Box maxWidth={260} width={0.49}>
            <PhoneNumber
              id={"3" as any}
              contactType={type}
              contact={contact}
              requireSms={requireSms}
              {...fieldProps}
            />
          </Box>
        </Grid>
      </Box>
      <Box mt={4}>
        <Grid container justify="space-between">
          <Box maxWidth={260} width={0.49}>
            <TextField
              name="dateOfBirthDt"
              label="Date of birth"
              defaultValue={contact.dateOfBirthDt}
              inputProps={{
                "data-testid": "ContactDetails__TextField--DateOfBirth",
              }}
              error={!!errors?.dateOfBirthDt}
              helperText={
                errors?.dateOfBirthDt
                  ? errors.dateOfBirthDt?.message
                  : "DD/MM/YYYY"
              }
              {...fieldProps}
              inputRef={(input) =>
                register(input, {
                  pattern: {
                    value: new RegExp(
                      // 01-31 / 01-12 / 1900-2099
                      "^([1-9]|0[1-9]|[12][0-9]|3[01])[/]([1-9]|0[1-9]|1[012])[/](19|20)\\d\\d$",
                    ),
                    message: "Please enter a valid date of birth DD/MM/YYYY",
                  },
                  validate: (string) =>
                    isFutureDate(parseDate(string))
                      ? "Date should not be in the future"
                      : true,
                })
              }
            />
          </Box>
          <Box maxWidth={260} width={0.49}>
            <TextField
              name="securityNumber"
              label="Security number"
              defaultValue={contact.securityNumber}
              inputProps={{
                "data-testid": "ContactDetails__TextField--SecurityNumber",
              }}
              {...fieldProps}
            />
          </Box>
        </Grid>
      </Box>
    </>
  );
};
